"use client"

import type { ElementType } from "react"

import { CarouselDots, DiscArrowUpRightIcon, Grid, Heading, Reel, Row, Text } from "@/components/ui"
import { TileImage } from "@/components/ui/tile-image"
import { Link } from "@/i18n/navigation"
import { itemAt } from "@/lib/helpers/array"
import { useReelPagination } from "@/lib/hooks/use-reel-pagination"

export type CutoutGround = "jade" | "brand" | "coral"

// Closed set, and derived from position rather than authored — jade → brand → coral is
// the order the design uses. Deriving it is what keeps this section on a payload api-v2
// already accepts: a `ground` field would be a schema change, and the section type is
// already all the backend coupling this design can afford.
const GROUNDS: Record<CutoutGround, string> = {
  jade: "bg-ground-jade",
  brand: "bg-ground-brand",
  coral: "bg-ground-coral",
}

const GROUND_ORDER: CutoutGround[] = ["jade", "brand", "coral"]

export interface CollectionCutout {
  id: string
  eyebrow?: string
  title: string
  image?: string
  imageAlt?: string
  href?: string
  serveId?: string
}

// A door into a category, and the one card in the system whose picture is not a
// photograph. The ground is a flat wash and the product is a cutout floating on it,
// so the card reads at any size and needs no scrim — which is what lets the copy sit
// in the open top-left corner instead of on a darkened strip.
//
// The cutout is authored as one composed image per card, already arranged and
// already the card's aspect, so the arrangement stays the designer's call rather
// than a set of rotation props nobody can preview.
//
// contain-and-anchor-bottom rather than cover: at the card's own aspect the two are
// identical, and when an author uploads a plate that isn't, the picture shrinks to
// fit instead of scaling up until it swallows the copy. There is no scrim to save it.
const CutoutCard = ({ item, index, priority }: { item: CollectionCutout; index: number; priority?: boolean }) => {
  const Card: ElementType = item.href ? Link : "div"
  const ground = GROUNDS[itemAt(GROUND_ORDER, index % GROUND_ORDER.length)]
  return (
    <Card
      {...(item.href ? { href: item.href } : {})}
      data-nelo-serve={item.serveId}
      className="press group focus-visible:ring-brand rounded-10x @3xl:rounded-12x aspect-cutout @container relative block min-w-0 overflow-hidden focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:outline-none"
    >
      <span aria-hidden className={`absolute inset-0 ${ground}`} />
      {item.image && (
        <TileImage
          src={item.image}
          alt={item.imageAlt ?? ""}
          fill
          sizes="(max-width: 768px) 82vw, 33vw"
          priority={priority}
          className="duration-slow ease-productive object-contain object-bottom transition-transform motion-safe:group-hover:scale-105"
        />
      )}
      {/* Padding lives here rather than on the card, so the @container above measures
          the unpadded box and the type step resolves against the real card width. */}
      <span className="absolute inset-0 flex flex-col gap-100 p-500">
        {item.eyebrow && (
          <Text as="span" variant="body" family="text" tone="inverted">
            {item.eyebrow}
          </Text>
        )}
        {/* tone on the element itself: globals.css sets a base colour on p/a/span, and
            a base rule beats inheritance, so an inverted wrapper would not cascade.
            No `balance` and no `max-w-title`: text-wrap:balance evens the lines instead of
            filling the width, which left 128px of a 405px box unused and pushed a third
            line onto the cutout. The card edge is the measure here. */}
        <Heading as="span" variant="sectionCardTitle" family="hero" tone="inverted" className="block">
          {/* Reserves the arrow's corner on the FIRST line only, so the headline still runs the
              full card width on every line below it. Shorter than one line box on purpose: a
              float only displaces the lines it intersects, and 24px sits inside the shortest
              line this type step produces (24px x 1.13). Without it the headline runs 21px
              under the arrow at the designed width. */}
          {item.href && <span aria-hidden className="float-right h-6 w-13" />}
          {item.title}
        </Heading>
      </span>
      {item.href && (
        <span
          aria-hidden
          className="bg-surface-paper text-brand duration-quick ease-productive absolute top-500 right-500 grid size-10 place-items-center rounded-full transition-transform motion-safe:group-hover:scale-110"
        >
          <DiscArrowUpRightIcon size={40} />
        </span>
      )}
    </Card>
  )
}

export const CollectionCutouts = ({ items, priority = false }: { items: CollectionCutout[]; priority?: boolean }) => {
  const { ref, index: page, stops, overflowing, goTo } = useReelPagination(items.length)
  return (
    <div className="@container">
      <Reel ref={ref} gap="300" snap inset className="@3xl:hidden">
        {items.map((item, index) => (
          <div key={item.id} className="w-4/5 shrink-0 snap-start">
            <CutoutCard item={item} index={index} priority={priority && index === 0} />
          </div>
        ))}
      </Reel>
      <Grid cols={3} colsDesktop={3} gap="500" className="px-gut hidden @3xl:grid">
        {items.map((item, index) => (
          <CutoutCard key={item.id} item={item} index={index} priority={priority && index === 0} />
        ))}
      </Grid>
      {/* Dots only where the reel actually overflows — three cards on a wide phone
          scroll, two on a narrow one may not, and a pager under a row that cannot
          move is a control that lies. Desktop is a grid, so it never has them. */}
      {overflowing && (
        <Row justify="center" className="mt-300 @3xl:hidden">
          <CarouselDots tone="plain" activeColor="bg-brand" count={stops} index={page} onChange={goTo} />
        </Row>
      )}
    </div>
  )
}
